import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { cloneDeep } from "lodash";
import { toast } from "react-toastify";
import {
  Box,
  Tab,
  Tabs,
  Button,
  Dialog,
  TextField,
  Typography,
  makeStyles,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import PropTypes from "prop-types";

import { setTemplate } from "../../../store/templateReducer";
import { setSingleTemplate } from "../../../store/singleTemplateReducer";
import store from "../../../store";
import TemplateFieldContainer from "../TemplateFieldContainer";
import { TemplateField, TemplateNestedField } from "../TemplateField";
import {
  filterTemplateName,
  exportSingleTemplate,
  importSingleTemplate,
  generateTemplatesFromJSON,
} from "../../../utils/templateUtils";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    display: "grid",
    gridTemplateColumns: "1fr 5fr",
    justifyContent: "space-between",
    height: 450,
    "& .name-of-template":{
      minWidth:300
    },
    "& .description-field":{
      width:"100%"
    }
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  tabsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "25px",
  },
  tabsItem: {
    flexGrow: 1,
  },
  btnWrapper: {
    display: "flex",
  },
  button: {
    margin: 10,
  },
  input: {
    display: "none",
  },
  tabItem: {
    textTransform: "unset",
  },
  tableLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  nameWrapper: {
    maxWidth: "170px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
}));

const TableLabel = ({
  name,
  onChange,
  onDelete,
  templateIndex,
  disabled = false,
  classes,
}) => {
  const onDeleteMethod = (e) => {
    e.stopPropagation();
    onDelete(templateIndex);
  };
  return (
    <div className={classes.tableLabel} onClick={() => onChange(templateIndex)}>
      <div className={classes.nameWrapper}>{name}</div>
      <Button onClick={onDeleteMethod} disabled={disabled}>
        <DeleteIcon />
      </Button>
    </div>
  );
};

const TemplateTabs = ({
  templates = [],
  setTemplates,
  addField,
  deleteField,
  changeField,
  changeName,
  deleteTemplate,
  currentTab = 0,
  setCurrentTab,
  setError,
  setIsChange,
  changeDescription,
}) => {
  const classes = useStyles();
  const importedTemplate = useSelector(
    ({ importedTemplate }) => importedTemplate
  );
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [templateNameForDialog, setTemplateNameForDialog] = useState("");
  const changeTab = (value) => {
    setCurrentTab(value);
  };
  const chosenTemplateNames = useSelector(({ file: { root = [] } }) =>
    root.flatMap(({ features = [] }) =>
      features.flatMap((item) => Object.keys(item))
    )
  );

  const handleDisagreeButton = () => {
    store.dispatch(setSingleTemplate([]));
    setDialogIsOpen(false);
  };

  const handleAgreeButton = () => {
    try {
      const importedTemplateName = Object.keys(...importedTemplate)[0];
      setTemplateNameForDialog(importedTemplateName);

      const templateListFromStore = cloneDeep(store.getState().templates);

      const listOfTemplateNames = templateListFromStore.map(
        (templateItem) => Object.keys(templateItem)[0]
      );

      const templateCrossingIndex = listOfTemplateNames.findIndex(
        (item) => item === importedTemplateName
      );

      templateListFromStore.splice(
        templateCrossingIndex,
        1,
        ...importedTemplate
      );

      setTemplates(generateTemplatesFromJSON(templateListFromStore));
      store.dispatch(setTemplate(templateListFromStore));
      store.dispatch(setSingleTemplate([]));
      setIsChange(true);
      setDialogIsOpen(false);
    } catch {
      toast.error(
        <div>
          <p>Something vent wrong.</p>
          <p>Recheck the structure of the imported template.</p>
        </div>
      );
    }
  };

  useEffect(() => {
    if (!importedTemplate.length) {
      return;
    }

    try {
      const importedTemplateName = Object.keys(importedTemplate[0])[0];
      setTemplateNameForDialog(importedTemplateName);

      const templateListFromStore = cloneDeep(store.getState().templates);

      const listOfTemplateNames = templateListFromStore.map(
        (templateItem) => Object.keys(templateItem)[0]
      );

      const templateCrossingIndex = listOfTemplateNames.findIndex(
        (item) => item === importedTemplateName
      );

      if (templateCrossingIndex === -1) {
        templateListFromStore.push(...importedTemplate);
      } else {
        setDialogIsOpen(true);
        return;
      }
      setTemplates(generateTemplatesFromJSON(templateListFromStore));
      store.dispatch(setTemplate(templateListFromStore));
      store.dispatch(setSingleTemplate([]));
      setIsChange(true);
    } catch {
      toast.error(
        <div>
          <p>Something vent wrong.</p>
          <p>Recheck the structure of the imported template.</p>
        </div>
      );
    }
  }, [importedTemplate]);

  return (
    <div className={classes.root}>
      <Dialog
        open={dialogIsOpen}
        onClose={() => {
          setDialogIsOpen(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Import template <b>{templateNameForDialog}</b>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to overwrite the <b>current</b>{" "}
            <i>"{templateNameForDialog}"</i> template?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDisagreeButton} color="primary">
            Disagree
          </Button>
          <Button onClick={handleAgreeButton} color="primary" autoFocus>
            Agree
          </Button>
        </DialogActions>
      </Dialog>
      <Tabs
        orientation="vertical"
        variant="scrollable"
        value={currentTab}
        className={classes.tabs}
      >
        {templates.map(({ name }, templateIndex) => (
          <Tab
            className={classes.tabItem}
            label={
              <TableLabel
                name={name.value}
                onDelete={deleteTemplate}
                onChange={changeTab}
                templateIndex={templateIndex}
                disabled={chosenTemplateNames.includes(name.value)}
                classes={classes}
              />
            }
            {...a11yProps(templateIndex)}
            key={templateIndex}
            value={templateIndex}
          />
        ))}
      </Tabs>
      {templates.map(({ name, fields = [], description }, templateIndex) => {
        // console.log(rest)
        return (
          <TabPanel
            value={currentTab}
            index={templateIndex}
            key={templateIndex}
            className={classes.tabsItem}
          >
            <header className={classes.tabsHeader}>
              <div>
                <TextField
                  error={name.error}
                  required
                  variant="outlined"
                  value={name.value}
                  label="Name of template"
                  className="name-of-template"
                  disabled={chosenTemplateNames.includes(name.value)}
                  onChange={({ target }) =>
                    changeName({
                      templateIndex: templateIndex,
                      name: {
                        value: filterTemplateName(target.value),
                        error: false,
                      },
                    })
                  }
                />
                {name.error ? (
                  <Typography color="error">
                    The name is required and must be unique
                  </Typography>
                ) : null}
              </div>
              <div className={classes.btnWrapper}>
                <Button
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  onClick={() => addField(templateIndex)}
                >
                  Add new field
                </Button>
                <Button
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  onClick={() => exportSingleTemplate(templateIndex, templates)}
                >
                  Export Template
                </Button>
                <div>
                  <input
                    accept=".json"
                    className={classes.input}
                    id="contained-button-file"
                    type="file"
                    onChange={(e) => {
                      importSingleTemplate(e);
                      e.target.value = "";
                    }}
                  />
                  <label htmlFor="contained-button-file">
                    <Button
                      className={classes.button}
                      variant="contained"
                      color="primary"
                      component="span"
                    >
                      Import Template
                    </Button>
                  </label>
                </div>
              </div>
            </header>
            <TextField
              multiline={true}
              variant="outlined"
              value={description?.value || ""}
              label="Description"
              className="description-field"
              onChange={({ target }) => {
                return changeDescription({
                  templateIndex: templateIndex,
                  description: {
                    value: target.value,
                    error: false,
                  },
                });
              }}
            />
            <TemplateFieldContainer>
              {fields.map((item, childIndex) => {
                if (Array.isArray(item.value.value)) {
                  return (
                    <TemplateNestedField
                      currentNode={item}
                      key={childIndex}
                      onDelete={deleteField}
                      onChange={changeField}
                      templateIndex={templateIndex}
                      childIndex={childIndex}
                      setTemplates={setTemplates}
                      templates={templates}
                      setError={setError}
                      setIsChange={setIsChange}
                    />
                  );
                } else {
                  return (
                    <TemplateField
                      currentNode={item}
                      key={childIndex}
                      onDelete={deleteField}
                      onChange={changeField}
                      templateIndex={templateIndex}
                      childIndex={childIndex}
                      setTemplates={setTemplates}
                      templates={templates}
                      setError={setError}
                      setIsChange={setIsChange}
                    />
                  );
                }
              })}
            </TemplateFieldContainer>
          </TabPanel>
        );
      })}
    </div>
  );
};

TemplateTabs.propTypes = {
  templates: PropTypes.array.isRequired,
  addField: PropTypes.func.isRequired,
  deleteField: PropTypes.func.isRequired,
  changeField: PropTypes.func.isRequired,
  changeName: PropTypes.func.isRequired,
  deleteTemplate: PropTypes.func.isRequired,
  currentTab: PropTypes.number.isRequired,
  setCurrentTab: PropTypes.func.isRequired,
};

export default TemplateTabs;
